import { useTranslation } from 'react-i18next';
import { Skeleton } from '@shared/ui';
import { formatLedgerAmount } from '@shared/lib/format-ledger-amount';
import { cn } from '@shared/lib/utils';

export interface BalanceMetricsGridProps {
  available: string | null;
  reserved: string | null;
  total: string | number | null;
  isLoading?: boolean;
  className?: string;
}

export function BalanceMetricsGrid({
  available,
  reserved,
  total,
  isLoading = false,
  className,
}: BalanceMetricsGridProps) {
  const { t } = useTranslation('cabinet');
  const unknown = t('balance.amountUnknown');

  const formatValue = (value: string | number | null) => {
    if (value == null) {
      return unknown;
    }

    return formatLedgerAmount(value, { assetLabel: '' });
  };

  const items = [
    { key: 'available', label: t('balance.available'), value: formatValue(available) },
    { key: 'reserved', label: t('balance.reserved'), value: formatValue(reserved) },
    { key: 'total', label: t('balance.total'), value: formatValue(total) },
  ];

  if (isLoading) {
    return (
      <div className={cn('grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3', className)}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-[5.5rem] w-full rounded-lg sm:h-[5.75rem] sm:rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div className={cn('grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3', className)}>
      {items.map((item) => (
        <div key={item.key} className="stat-card flex flex-col justify-between gap-2">
          <p className="stat-card-label leading-snug">{item.label}</p>
          <p className="font-sans text-xl font-semibold tabular-nums leading-none text-base-foreground sm:text-2xl">
            {item.value}
          </p>
        </div>
      ))}
    </div>
  );
}
